import React, { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { ActionType, AppContext, TRACKS, Branches, INTEGRATION_EVENT_TYPE } from '../../interfaces';
import { Context } from '../../context/Context';
import { getCampaignByName, getDomainParams, getImagesByBrand, redirectTo, searchAccount } from '../../utils/Utils';
import HeliosClient from '../../api/Helios';
import {
	Brand,
	HIDE_SPLASH_LOGO_WHEN_WEBVIEW_BRANDS,
	WHITELABEL_BRANDS,
	BranchDefaultCoords,
	ACCOUNTS_WITH_EXTERNAL_INFO_FILLED,
} from '../../utils/constants';
import {
	setBrand,
	setCurrentTrack,
	setServiceTypes,
	setuserExternalParams,
	setAccount,
	setSituationCoords,
	setCampaign,
	setIntegrations,
} from '../../context/actions';

import HomeIcon from '../../assets/imgs/home_icon.svg';
import ConnectBG from '../../assets/imgs/connect-background.png';

import './Splash.scss';
import i18n from '../../utils/i18n';

const TIME_TO_FETCH = 3000;

const SplashScreen: FunctionComponent = (): JSX.Element => {
	const {
		dispatch,
		brand,
		service,
		currentTrack,
		userExternalParams,
		account: { branch },
		isMMMHOD,
	} = useContext(Context) as AppContext;
	const [visible, setVisible] = useState<boolean>(false);
	const [isCorporate, setIsCorporate] = useState<boolean>(false);
	const location = useLocation();
	const history = useHistory();

	const cleanDataIfCurrentServiceIsExpired = useCallback(async () => {
		if (service._id) {
			const {
				data: { data },
			} = await new HeliosClient().getService(service._id);

			const serviceDate = moment(data.schedule || data.date);
			const hoursFromServiceDate = moment().diff(serviceDate, 'hours');
			if (['Finished', 'Cancelled', 'Audit', 'Void'].includes(data.status) || hoursFromServiceDate > 24) {
				dispatch({ type: ActionType.CLEAR_ALL });
				setBrand({ brand }, dispatch);
			}
		}
	}, [dispatch, service._id, brand]);

	// Buscar la manera de pasarlo al App.tsx
	useEffect(() => {
		console.log('Main integrations epark');
		// Con esta funcion verificamos si el usuario viene de ej. https://hondaasistencia.com
		// asi poder traer el account y brand.

		const {
			accountId,
			brandName,
			isCorporateStatus,
			plate,
			fullName,
			isFromWebview,
			isFromCheckLimit,
			track,
			campaign,
			policyNumber,
			event,
			lang,
		} = getDomainParams(window.location.href, location.search, branch || '');

		// If the brand retrieved from the URL matches the one in the context, app state is cleared.
		// It is worth noting that the brand from context is first obtained from local storage.
		if (isFromWebview || brandName !== brand) {
			if (isFromCheckLimit) {
				dispatch({ type: ActionType.CLEAR_ALL_FOR_CHECK_LIMIT });
			} else if (isMMMHOD) {
				dispatch({ type: ActionType.CLEAR_ALL_FOR_MMM_HOD });
			} else {
				dispatch({ type: ActionType.CLEAR_ALL });
			}
		}

		if (!ACCOUNTS_WITH_EXTERNAL_INFO_FILLED.includes(brandName)) {
			setuserExternalParams(plate, fullName, isFromWebview, policyNumber, dispatch);
		}

		setIsCorporate(isCorporateStatus);

		setBrand({ brand: brandName, lang: lang ?? undefined }, dispatch);
		setVisible(true);

		const newTrack = track === TRACKS.HOME || brand === Brand.HOME_ON_DEMAND_PR ? TRACKS.HOME : TRACKS.ROAD;
		// If the track is different, the category property of the context is cleared.
		if (currentTrack !== newTrack) {
			dispatch({ type: ActionType.CLEAR_CATEGORY });
		}
		setCurrentTrack(newTrack, dispatch);

		if (
			[INTEGRATION_EVENT_TYPE.SERVICE_CREATED, INTEGRATION_EVENT_TYPE.TRACKING_COMPLETED].includes(
				event as INTEGRATION_EVENT_TYPE
			)
		) {
			// Service created and redirected to /integrations: Clear all and set brand
			dispatch({ type: ActionType.CLEAR_ALL });
			setBrand({ brand, lang: lang ?? undefined }, dispatch);
		} else {
			const timer = setTimeout(async () => {
				await cleanDataIfCurrentServiceIsExpired();
				const serviceTypes = await new HeliosClient().fetchServiceTypes();
				setServiceTypes(dispatch, serviceTypes.data.data);
				const account = await searchAccount(accountId);
				setAccount(account, dispatch);

				if (campaign) {
					const campaignData = await getCampaignByName(campaign);
					setCampaign(campaignData, dispatch);
				}

				// Retrieves and populates Integrations configuration for an specific account
				fetchAndSetIntegrations(accountId);

				// Set the default map coordinates depending on the account
				if (!service._id) {
					const defaultCoords = BranchDefaultCoords[account.branch as Branches] ?? BranchDefaultCoords[Branches.PR];
					setSituationCoords({ ...defaultCoords, placeId: '' }, dispatch);
				}
				if (brand === Brand.OTIUM && newTrack === TRACKS.HOME) {
					return history.replace('/plate');
				} else {
					redirectTo(history, isCorporate, brand);
				}
			}, TIME_TO_FETCH);

			return (): void => clearTimeout(timer);
		}

		// eslint-disable-next-line
	}, [dispatch, location.search, branch, history, isCorporate, brand, cleanDataIfCurrentServiceIsExpired]);

	const fetchAndSetIntegrations = async (accountId: string) => {
		try {
			const {
				data: { data: integrations },
			} = await new HeliosClient().fetchAccountIntegrations(accountId);
			setIntegrations(integrations, dispatch);
		} catch (err) {
			console.log('Error Getting Integrations: ', err);
		}
	};

	const classname = cn({
		hidden: !visible,
		'splash-screen': visible,
	});

	const displayCopy = (): JSX.Element => {
		if (['connect-home-repair-ondemand-pr', 'otium'].includes(brand)) {
			return (
				<p>
					{i18n.get('screens.splash.title', 'Mantiene, Repara y Mejora')}
					<br />
					{i18n.get('screens.splash.prefix', 'tu')} <span>{i18n.get('screens.splash.subtitle', 'Hogar')}</span>
				</p>
			);
		} else {
			return (
				<p>
					Asistencia <br /> en solo <span>minutos</span>
				</p>
			);
		}
	};

	const displayImg = [Brand.HOME_ON_DEMAND_PR, Brand.OTIUM].includes(brand as Brand) ? HomeIcon : ConnectBG;
	const hideLogo = userExternalParams?.isFromWebview && HIDE_SPLASH_LOGO_WHEN_WEBVIEW_BRANDS.includes(brand);
	const isNotConnectBrand = !WHITELABEL_BRANDS.includes(brand);
	return (
		<div className={`splash-background-color ${classname}`}>
			{!hideLogo ? (
				<div
					className={cn('splash-screen__header', {
						'flex-2': WHITELABEL_BRANDS.includes(brand),
					})}
				>
					<figure className="splash-screen__logo">
						<img
							width="200"
							className="splash-screen__logo-image"
							src={getImagesByBrand(brand).logoDark}
							alt="Connect Logo"
						/>
					</figure>
					{isNotConnectBrand && displayCopy()}
				</div>
			) : null}
			<figure className="splash-screen__bg">
				{isNotConnectBrand && (
					<img
						width="400"
						height="300"
						className="splash-screen__bg-image"
						src={displayImg}
						alt="Connect Assistance Background"
					/>
				)}
			</figure>
			{WHITELABEL_BRANDS.includes(brand) && (
				<div className={`splash-screen__footer ${hideLogo ? 'hidden-logo' : ''}`}>
					<div className="splash-screen__loader loader-color border-right-transparent-color" />
				</div>
			)}
		</div>
	);
};

export default SplashScreen;
